import { useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HardHat, Plus, Trash2, Save, Loader2, Lock } from "lucide-react";

// Super-admin manager for the crew-role labels vendors can choose during onboarding.
// The three built-in roles carry real dispatch behaviour and can't be edited or removed;
// custom labels are contact tags with no auto-dispatch effect.
export default function VendorRoles() {
  const { user } = useAuth();
  const allowed = user?.role === "super_admin";
  const [builtins, setBuiltins] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/fs-crew-roles");
      setBuiltins((data.roles || []).filter((r) => r.builtin));
      setCustom((data.roles || []).filter((r) => !r.builtin).map((r) => ({ ...r })));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load roles"); }
    setLoading(false);
  };
  useEffect(() => { if (allowed) load(); }, [allowed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="vendor-roles-denied">
        <Lock className="mx-auto h-8 w-8 text-ink/30" />
        <p className="mt-3 text-sm font-semibold text-ink/60">Vendor crew roles are managed by Redline super admins only.</p>
      </div>
    );
  }

  const addRow = () => setCustom((p) => [...p, { key: "", label: "" }]);
  const setRow = (i, label) => setCustom((p) => p.map((r, j) => (j === i ? { ...r, label } : r)));
  const removeRow = (i) => setCustom((p) => p.filter((_, j) => j !== i));

  const save = async () => {
    const payload = custom
      .map((r) => ({ key: r.key || "", label: (r.label || "").trim() }))
      .filter((r) => r.label);
    setSaving(true);
    try {
      const { data } = await api.put("/admin/fs-crew-roles", { custom: payload });
      setBuiltins((data.roles || []).filter((r) => r.builtin));
      setCustom((data.roles || []).filter((r) => !r.builtin).map((r) => ({ ...r })));
      toast.success("Vendor roles saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    setSaving(false);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8" data-testid="vendor-roles-page">
      <div className="mb-6 flex items-center gap-2">
        <HardHat className="h-6 w-6 text-redline-500" strokeWidth={1.5} />
        <PageTitle title="Vendor crew roles" />
      </div>
      <p className="mb-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        These are the roles a vendor can assign to each person during onboarding. The three built-in
        roles drive real dispatch logic and can't be changed. Add your own labels (like
        &ldquo;Foreman&rdquo; or &ldquo;After-hours&rdquo;) when you need them — custom labels are just
        contact tags and <span className="font-semibold">don&apos;t receive jobs automatically</span>.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-ink/30" /></div>
      ) : (
        <>
          <div className="mb-6 rounded-2xl border border-sand-200 bg-white p-5">
            <div className="mb-3 text-xs font-bold uppercase tracking-wider text-ink/45">Built-in — routing roles</div>
            <div className="space-y-2">
              {builtins.map((r) => (
                <div key={r.key} data-testid={`builtin-role-${r.key}`}
                  className="flex items-center justify-between gap-2 rounded-xl bg-sand-50 px-4 py-2.5">
                  <div>
                    <div className="text-sm font-semibold text-ink">{r.label}</div>
                    <div className="font-mono text-xs text-ink/40">{r.key}</div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-ink/40">
                    <Lock className="h-3.5 w-3.5" /> Locked
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-sand-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-ink/45">Custom labels</div>
              <button data-testid="add-role-btn" onClick={addRow}
                className="inline-flex items-center gap-1.5 rounded-lg border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/70 hover:border-ink/30">
                <Plus className="h-3.5 w-3.5" /> Add a role
              </button>
            </div>
            {custom.length === 0 && (
              <p className="py-4 text-center text-sm text-ink/40">No custom roles yet.</p>
            )}
            <div className="space-y-2">
              {custom.map((r, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`custom-role-row-${i}`}>
                  <input data-testid={`custom-role-input-${i}`} value={r.label || ""}
                    placeholder="Role name (e.g. Foreman)" onChange={(e) => setRow(i, e.target.value)}
                    className="flex-1 rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                  {r.key && <span className="font-mono text-xs text-ink/35">{r.key}</span>}
                  <button data-testid={`remove-role-${i}`} onClick={() => removeRow(i)}
                    className="rounded-lg p-2 text-ink/30 hover:bg-redline-50 hover:text-redline-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <button data-testid="save-roles-btn" onClick={save} disabled={saving}
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-redline-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-redline-600 disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save roles
            </button>
          </div>
        </>
      )}
    </div>
  );
}
